// Global Instructions Rule Applied!

import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { Tag, Typography } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { FormTable } from '../../../../../core/components/FormTable/index.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, isRecordFieldValue, type ValuationFieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { useCurrencies } from '../../../../currencies/useCurrencies.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import dayjs from 'dayjs'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { RoundEntryFields } from '../../../../../state_manager/data_models/round_entry.ts'
import { formatNumberNoDecimal } from '../../../../../core/components/InputManager/utils/formatters.ts'
import { addRound } from '../../../../../state_manager/state_update_actions/rounds/index.ts'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { selectCompanyById } from '../../../../../state_manager/state_update_actions/companies/selectors.ts'
import { getRoundFields } from '../../../utils/fieldConfigs.ts'
import { LOOKUP_CATEGORY_FUNDING_STAGE } from '../../../../../state_manager/constants/lookupCategories.ts'
import ExportRowsButton from '../../../../shared/table_grid_export/ExportRowsButton.tsx'

const { Link } = Typography

interface FundingRoundsTableProps {
  companyData?: Record<string, any> | null
  className?: string
  recordId?: string | null
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
}

interface FundingRoundRow {
  key: string
  name: string
  date: string | null
  valuation: number | null
  valuationType: string | null
  size: number | null
  stage: string
  our_investment: number | null
  currencyId: string | null
  roundData?: any
}

export function FundingRoundsTable({
  companyData,
  className = '',
  recordId,
  handleFieldChange,
  onPatchSuccess
}: FundingRoundsTableProps) {
  const { getCurrencyById } = useCurrencies()
  const { getLookupLabel, getLookupOptions } = useLookups()
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()

  // State for row editing
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null)
  const [savingRowKey, setSavingRowKey] = useState<string | null>(null)
  const [originalRoundData, setOriginalRoundData] = useState<Record<string, any> | null>(null)
  const [firstErrorFieldKey, setFirstErrorFieldKey] = useState<{ rowKey: string; fieldKey: string } | null>(null)
  const [roundFieldValues, setRoundFieldValues] = useState<Record<string, Record<string, any>>>({})

  // Get latest company from Redux state to ensure we have current rounds data
  const latestCompanyFromRedux = useSelector((state: any) => (recordId ? selectCompanyById(state, recordId) : null))

  // Prioritize Redux state over props to ensure sync with Redux
  const data = latestCompanyFromRedux || companyData || {}

  // Extract funding rounds data from prog__rounds array
  const roundsArray = useMemo(() => {
    if (!data) return []
    return data[AccountRecordFields.prog__rounds] || data['prog__rounds'] || []
  }, [data])

  // Extract funding rounds data from prog__rounds array
  const fundingRounds = useMemo<FundingRoundRow[]>(() => {
    if (!Array.isArray(roundsArray) || roundsArray.length === 0) {
      return []
    }

    // Map each round from prog__rounds to FundingRoundRow
    return roundsArray.map((round: any) => {
      const fundingStageId = round[RoundEntryFields.rounds__funding_stage]
      const fundingStageLabel = fundingStageId ? getLookupLabel(fundingStageId) : '-'

      return {
        key: round[RoundEntryFields.rounds__id] || `round-${Math.random()}`,
        name: round[RoundEntryFields.rounds__name] || '-',
        date: round[RoundEntryFields.rounds__round_close_date] || null,
        valuation: round[RoundEntryFields.prog__valuation] || null,
        valuationType: round[RoundEntryFields.prog__valuation_type] || null,
        size: round[RoundEntryFields.rounds__raise_amount] || null,
        stage: fundingStageLabel,
        our_investment: round[RoundEntryFields.prog__commitment] || null,
        currencyId: round[RoundEntryFields.rounds__currency] || null,
        roundData: round // Store full round object for navigation
      }
    })
  }, [roundsArray, getLookupLabel])

  // Handle round name click - store in Redux and navigate
  const handleRoundNameClick = useCallback(
    (roundData: any) => {
      if (!roundData) return

      const roundId = roundData[RoundEntryFields.rounds__id]
      const roundName = roundData[RoundEntryFields.rounds__name] || 'Round'

      if (roundId) {
        // Store round in Redux state
        dispatch(addRound({ round: roundData }))
        // Navigate to rounds form
        openFormInTab('rounds', roundId, roundName)
      }
    },
    [dispatch, openFormInTab]
  )

  // Handle round patch success - update company rounds array in Redux
  const handleRoundPatchSuccess = useCallback(
    (patchRoundId: string, updates: Record<string, any>) => {
      if (!recordId) return

      // Get latest company data from Redux to ensure we have current rounds
      const currentCompanyData = latestCompanyFromRedux || companyData || {}
      const currentRounds = currentCompanyData[AccountRecordFields.prog__rounds] || []
      const currentRecentRound = currentCompanyData[AccountRecordFields.prog__recent_round] || null
      const currentLatestRound = currentCompanyData[AccountRecordFields.prog__latest_round] || null

      // Update the specific round in the company's rounds array
      let updatedRound: any = null
      const updatedRounds = currentRounds.map((round: any) => {
        if (round[RoundEntryFields.rounds__id] === patchRoundId) {
          // Merge updates into the round object
          updatedRound = {
            ...round,
            ...updates
          }
          // Ensure field names match the expected format
          if (updates.pre_money_valuation !== undefined) {
            updatedRound[RoundEntryFields.rounds__pre_money_valuation] = updates.pre_money_valuation
          }
          if (updates.post_money_valuation !== undefined) {
            updatedRound[RoundEntryFields.rounds__post_money_valuation] = updates.post_money_valuation
          }
          if (updates.name !== undefined) {
            updatedRound[RoundEntryFields.rounds__name] = updates.name
          }
          if (updates.round_close_date !== undefined) {
            updatedRound[RoundEntryFields.rounds__round_close_date] = updates.round_close_date
          }
          if (updates.raise_amount !== undefined) {
            updatedRound[RoundEntryFields.rounds__raise_amount] = updates.raise_amount
          }
          if (updates.funding_stage !== undefined) {
            updatedRound[RoundEntryFields.rounds__funding_stage] = updates.funding_stage
          }
          if (updates.commitment !== undefined) {
            updatedRound[RoundEntryFields.prog__commitment] = updates.commitment
          }
          return updatedRound
        }
        return round
      })

      // Update company record with updated rounds array
      const companyUpdates: Record<string, any> = {
        [AccountRecordFields.prog__rounds]: updatedRounds
      }

      // Update computed fields if the updated round is the recent/latest round
      if (updatedRound) {
        // Update prog__recent_round if this is the recent round
        if (currentRecentRound && currentRecentRound[RoundEntryFields.rounds__id] === patchRoundId) {
          companyUpdates[AccountRecordFields.prog__recent_round] = updatedRound
        }

        // Update prog__latest_round if this is the latest round
        if (currentLatestRound && currentLatestRound[RoundEntryFields.rounds__id] === patchRoundId) {
          companyUpdates[AccountRecordFields.prog__latest_round] = updatedRound
        }
      }

      // Also update individual fields if they exist in updates (for computed fields)
      Object.keys(updates).forEach((key) => {
        if (key.startsWith('rounds__') || key.startsWith('prog__')) {
          companyUpdates[key] = updates[key]
        }
      })

      // Update Redux state directly
      dispatch(updateCompanyRecord({ companyId: recordId, updates: companyUpdates }))

      // Also call parent callback if provided
      if (onPatchSuccess) {
        onPatchSuccess(recordId, companyUpdates)
      }

      // Cancel editing after successful patch
      // Note: patchRoundId is the round ID, and editingRowKey is also the round ID (row key)
      if (editingRowKey === patchRoundId) {
        setEditingRowKey(null)
        setSavingRowKey(null)
        setOriginalRoundData(null)
        setFirstErrorFieldKey(null)
      } else {
        // If editingRowKey doesn't match, still clear saving state (might be a race condition)
        // Find the row that matches the patch round ID and clear its saving state
        setSavingRowKey((current) => {
          if (!current) return null
          // Current is the row key (round ID), so check if it matches patchRoundId
          return current === patchRoundId ? null : current
        })
      }
    },
    [recordId, latestCompanyFromRedux, companyData, onPatchSuccess, dispatch, editingRowKey]
  )

  // Helper functions for InputManager field configurations
  const helpers = useMemo(
    () => ({
      getCurrencyById,
      getLookupOptions
    }),
    [getCurrencyById, getLookupOptions]
  )

  // Custom hyperlink handler for rounds that stores round data in Redux first
  const handleRoundHyperlinkClick = useCallback(
    (module: string, roundId: string, title: string, roundData?: any) => {
      // If we have roundData, store it in Redux first
      if (roundData && roundId) {
        dispatch(addRound({ round: roundData }))
      }
      // Then open the form
      openFormInTab(module, roundId, title)
    },
    [dispatch, openFormInTab]
  )

  // Function to scroll table container to the left
  const scrollTableToLeft = useCallback(() => {
    // Try multiple selectors to find the scrollable table container
    const selectors = [
      '.ant-table-content',
      '.ant-table-container .ant-table-content',
      '.ant-table-body',
      '.ant-table-container',
      '.ant-table-tbody'
    ]

    let tableContainer = null
    for (const selector of selectors) {
      const element = document.querySelector(selector)
      if (element && element.scrollWidth > element.clientWidth) {
        tableContainer = element
        break
      }
    }

    if (tableContainer) {
      // Scroll table to the left using both methods for reliability
      tableContainer.scrollTo({ left: 0, behavior: 'smooth' })
      tableContainer.scrollLeft = 0
    }
  }, [])

  // Start editing a row
  const startEditing = useCallback(
    (rowKey: string) => {
      // Don't allow editing if another row is being edited
      if (editingRowKey && editingRowKey !== rowKey) {
        return
      }

      // Store original data for potential cancel
      const originalRound = fundingRounds.find((round) => round.key === rowKey)
      if (originalRound && originalRound.roundData) {
        setOriginalRoundData({ ...originalRound.roundData })
      }

      setEditingRowKey(rowKey)
      // Auto-focus Name field (first field) when editing starts
      setFirstErrorFieldKey({ rowKey, fieldKey: RoundEntryFields.rounds__name })
      // Scroll table to the left
      scrollTableToLeft()
    },
    [editingRowKey, fundingRounds, scrollTableToLeft]
  )

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingRowKey(null)
    setOriginalRoundData(null)
    setFirstErrorFieldKey(null)
    // Clear tracked field values for the row being canceled
    if (editingRowKey) {
      setRoundFieldValues((prev) => {
        const updated = { ...prev }
        delete updated[editingRowKey]
        return updated
      })
    }
  }, [editingRowKey])

  // Auto-focus first field when editing starts
  useEffect(() => {
    if (firstErrorFieldKey && editingRowKey === firstErrorFieldKey.rowKey) {
      // Small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        const fieldKey = firstErrorFieldKey.fieldKey

        // Find the table row using Ant Design's rowKey attribute
        const tableRow = document.querySelector(`tr[data-row-key="${firstErrorFieldKey.rowKey}"]`)
        if (tableRow) {
          // Find all cells in the row (excluding actions column which is last)
          const cells = Array.from(tableRow.querySelectorAll('td'))

          // Map field keys to column order (matching the column order)
          const fieldToColumnMap: Record<string, number> = {
            [RoundEntryFields.rounds__name]: 0,
            [RoundEntryFields.rounds__round_close_date]: 1,
            [RoundEntryFields.prog__valuation]: 2,
            [RoundEntryFields.rounds__raise_amount]: 3,
            [RoundEntryFields.rounds__funding_stage]: 4,
            [RoundEntryFields.prog__commitment]: 5
          }

          // Find the cell that contains the field we want to focus
          const columnIndex = fieldToColumnMap[fieldKey]
          if (columnIndex !== undefined) {
            // Find cells with inputs (excluding action buttons)
            const dataIndexCells = cells.filter((cell) => {
              return (
                cell.querySelector('input, textarea, .ant-select-selector, .ant-select') &&
                !cell.querySelector('button[class*="ant-btn"]')
              )
            })

            if (dataIndexCells[columnIndex]) {
              const targetCell = dataIndexCells[columnIndex]
              // Find input elements within the cell
              const input = targetCell.querySelector<HTMLElement>('input, textarea, .ant-select-selector, .ant-select')
              if (input) {
                // For Ant Design Select, focus the selector
                if (input.classList.contains('ant-select')) {
                  const selector = input.querySelector<HTMLElement>('.ant-select-selector')
                  if (selector) {
                    selector.focus()
                    // Also trigger click to open dropdown if needed
                    setTimeout(() => selector.click(), 50)
                  }
                } else {
                  input.focus()
                }
              }
            }
          }
        }
      }, 200) // Delay to ensure DOM updates are complete

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [firstErrorFieldKey, editingRowKey])

  // Save row - collects all field values and makes direct API call
  const saveRow = useCallback(
    async (rowKey: string) => {
      const roundToSave = fundingRounds.find((round) => round.key === rowKey)
      if (!roundToSave || !roundToSave.roundData) {
        setSavingRowKey(null)
        return false
      }

      const roundId = roundToSave.roundData[RoundEntryFields.rounds__id]
      if (!roundId || !recordId) {
        setSavingRowKey(null)
        return false
      }

      setSavingRowKey(rowKey)

      try {
        // Get tracked field values for this row
        const trackedValues = roundFieldValues[rowKey] || {}

        // Get all field configs for this round
        const roundFields = getRoundFields(roundToSave.roundData, helpers, false)

        // Collect current values - use tracked values if available, otherwise use original roundData
        const updates: Record<string, any> = { id: roundId }

        for (const field of roundFields) {
          // Use tracked value if available, otherwise use original value
          const value =
            trackedValues[field.key] !== undefined ? trackedValues[field.key] : roundToSave.roundData[field.key]

          if (value !== undefined) {
            updates[field.key] = value
          }
        }

        // Transform updates using valueTransformers from field configs
        const apiBody: Record<string, any> = { id: roundId }
        const currentRoundData = { ...roundToSave.roundData, ...updates }

        // Apply value transformers for each field
        for (const field of roundFields) {
          if (field.key in updates && (field as any).valueTransformer) {
            const transformer = (field as any).valueTransformer
            const transformed = transformer(updates[field.key], currentRoundData)

            // Extract API field names (without '__')
            Object.keys(transformed).forEach((key) => {
              if (!key.includes('__') && key !== 'id') {
                apiBody[key] = transformed[key]
              }
            })
          } else if (field.key === RoundEntryFields.rounds__name && updates[field.key] !== undefined) {
            apiBody.name = updates[field.key]
          } else if (field.key === RoundEntryFields.rounds__round_close_date && updates[field.key] !== undefined) {
            apiBody.round_close_date = updates[field.key]
          } else if (field.key === RoundEntryFields.rounds__raise_amount && updates[field.key] !== undefined) {
            apiBody.raise_amount = updates[field.key]
          } else if (field.key === RoundEntryFields.rounds__funding_stage && updates[field.key] !== undefined) {
            apiBody.funding_stage = updates[field.key]
          }
        }

        // Valuation fields are handled by valueTransformer, but ensure they're in apiBody
        if (
          updates[RoundEntryFields.rounds__pre_money_valuation] !== undefined ||
          updates[RoundEntryFields.rounds__post_money_valuation] !== undefined
        ) {
          // These should already be set by valueTransformer, but ensure they're present
          if (
            apiBody.pre_money_valuation === undefined &&
            updates[RoundEntryFields.rounds__pre_money_valuation] !== undefined
          ) {
            apiBody.pre_money_valuation = updates[RoundEntryFields.rounds__pre_money_valuation]
          }
          if (
            apiBody.post_money_valuation === undefined &&
            updates[RoundEntryFields.rounds__post_money_valuation] !== undefined
          ) {
            apiBody.post_money_valuation = updates[RoundEntryFields.rounds__post_money_valuation]
          }
        }

        // Make API call
        const { getAuthHeaders } = await import('../../../../../core/lib/apiRequest.js')
        const headers = await getAuthHeaders()
        const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'
        const response = await fetch(`${baseUrl}/rounds`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(apiBody)
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || `Failed to save round: ${response.status}`)
        }

        const result = await response.json()

        // Call handleRoundPatchSuccess with the updates
        if (result.data) {
          // Transform API response to Redux format
          const reduxUpdates: Record<string, any> = {}
          Object.keys(updates).forEach((key) => {
            if (key.includes('__')) {
              reduxUpdates[key] = updates[key]
            }
          })

          // Map API fields to Redux fields
          if (apiBody.name !== undefined) {
            reduxUpdates[RoundEntryFields.rounds__name] = apiBody.name
          }
          if (apiBody.round_close_date !== undefined) {
            reduxUpdates[RoundEntryFields.rounds__round_close_date] = apiBody.round_close_date
          }
          if (apiBody.raise_amount !== undefined) {
            reduxUpdates[RoundEntryFields.rounds__raise_amount] = apiBody.raise_amount
          }
          if (apiBody.funding_stage !== undefined) {
            reduxUpdates[RoundEntryFields.rounds__funding_stage] = apiBody.funding_stage
          }
          if (apiBody.pre_money_valuation !== undefined) {
            reduxUpdates[RoundEntryFields.rounds__pre_money_valuation] = apiBody.pre_money_valuation
          }
          if (apiBody.post_money_valuation !== undefined) {
            reduxUpdates[RoundEntryFields.rounds__post_money_valuation] = apiBody.post_money_valuation
          }

          handleRoundPatchSuccess(roundId, reduxUpdates)
        }

        return true
      } catch (error) {
        console.error('Failed to save round:', error)
        setSavingRowKey(null)
        return false
      }
    },
    [fundingRounds, recordId, helpers, handleRoundPatchSuccess, roundFieldValues]
  )

  // Dummy remove function - not used but required by FormTable to show actions column
  const handleRemove = useCallback(() => {
    // No-op - funding rounds are not deleted from this table
  }, [])

  // Handle field change - track values in state for save
  const handleFieldChangeForRound = useCallback((rowKey: string, fieldKey: string, value: any) => {
    setRoundFieldValues((prev) => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        [fieldKey]: value
      }
    }))
  }, [])

  // Get field config for a specific round and field key
  const getFieldConfigForRound = useCallback(
    (roundData: any, fieldKey: string, isEditing: boolean, rowKey?: string) => {
      const roundId = roundData?.[RoundEntryFields.rounds__id]
      // Fields are editable only when row is in edit mode and we have a roundId
      const isReadOnly = !roundId || !recordId || !isEditing
      const roundFields = getRoundFields(roundData, helpers, isReadOnly)
      const fieldConfig = roundFields.find((field) => field.key === fieldKey)
      // Check if this field should be auto-focused
      const shouldAutoFocus = firstErrorFieldKey?.rowKey === rowKey && firstErrorFieldKey?.fieldKey === fieldKey
      // Make key unique per row and ensure field is editable based on editing state
      if (fieldConfig && roundId) {
        // For round name field, add custom hyperlink handler that stores round data
        if (fieldKey === RoundEntryFields.rounds__name && 'hyperlinked' in fieldConfig && fieldConfig.hyperlinked) {
          return {
            ...fieldConfig,
            key: `${roundId}-${fieldConfig.key}`, // Make key unique per row to prevent shared editing state
            // Only override editable if it's not explicitly set to false (for read-only fields like commitment)
            editable: fieldConfig.editable === false ? false : isEditing,
            autoFocus: shouldAutoFocus,
            // Add custom hyperlink handler that stores round data
            onHyperlinkClick: (module: string, id: string, title: string) => {
              handleRoundHyperlinkClick(module, id, title, roundData)
            }
          }
        }
        return {
          ...fieldConfig,
          key: `${roundId}-${fieldConfig.key}`, // Make key unique per row to prevent shared editing state
          // Only override editable if it's not explicitly set to false (for read-only fields like commitment)
          editable: fieldConfig.editable === false ? false : isEditing,
          autoFocus: shouldAutoFocus
        }
      }
      return fieldConfig
    },
    [helpers, recordId, handleRoundHyperlinkClick, firstErrorFieldKey]
  )

  // Column configuration for funding rounds table
  const columnConfig = useMemo(() => {
    return [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: editingRowKey ? 250 : 200,
        minWidth: editingRowKey ? 250 : 200,
        render: (value: string, record: FundingRoundRow) => {
          const roundId = record.roundData?.[RoundEntryFields.rounds__id]
          const isEditing = editingRowKey === record.key

          if (!roundId) {
            // Fallback: show clickable link if no roundId
            if (!value || value === '-') return '-'
            return (
              <Link onClick={() => handleRoundNameClick(record.roundData)} style={{ cursor: 'pointer' }}>
                {value}
              </Link>
            )
          }

          const fieldConfig = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.rounds__name,
            isEditing,
            record.key
          )
          if (!fieldConfig) {
            // Fallback: show clickable link if no field config
            if (!value || value === '-') return '-'
            return (
              <Link onClick={() => handleRoundNameClick(record.roundData)} style={{ cursor: 'pointer' }}>
                {value}
              </Link>
            )
          }

          if (!isEditing) {
            return (
              <div onClick={() => startEditing(record.key)} className='cursor-pointer'>
                <InputManager
                  inputMode='read'
                  labelPosition='none'
                  alignment='left'
                  fields={[fieldConfig]}
                  onChange={(key, val) => handleFieldChange?.(key, val)}
                  className='cell-edit-input-manager'
                  currentRecord={record.roundData}
                />
              </div>
            )
          }

          return (
            <InputManager
              inputMode='edit'
              labelPosition='none'
              alignment='left'
              fields={[fieldConfig]}
              onChange={(key, val) => {
                handleFieldChangeForRound(record.key, RoundEntryFields.rounds__name, val)
                handleFieldChange?.(key, val)
              }}
              enableAutoPatch={false}
              patchId={roundId}
              recordId={roundId || ''}
              onPatchSuccess={handleRoundPatchSuccess}
              className='cell-edit-input-manager'
              currentRecord={record.roundData}
            />
          )
        }
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: editingRowKey ? 200 : 150,
        minWidth: editingRowKey ? 200 : 150,
        render: (value: string | null, record: FundingRoundRow) => {
          const roundId = record.roundData?.[RoundEntryFields.rounds__id]
          const isEditing = editingRowKey === record.key

          if (!roundId) return value ? dayjs(value).format('MMM DD, YYYY') : '-'

          const fieldConfig = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.rounds__round_close_date,
            isEditing,
            record.key
          )
          if (!fieldConfig) return value ? dayjs(value).format('MMM DD, YYYY') : '-'

          if (!isEditing) {
            return (
              <div onClick={() => startEditing(record.key)} className='cursor-pointer'>
                <InputManager
                  inputMode='read'
                  labelPosition='none'
                  alignment='left'
                  fields={[fieldConfig]}
                  onChange={(key, val) => handleFieldChange?.(key, val)}
                  className='cell-edit-input-manager'
                  currentRecord={record.roundData}
                />
              </div>
            )
          }

          return (
            <InputManager
              inputMode='edit'
              labelPosition='none'
              alignment='left'
              fields={[fieldConfig]}
              onChange={(key, val) => {
                handleFieldChangeForRound(record.key, RoundEntryFields.rounds__round_close_date, val)
                handleFieldChange?.(key, val)
              }}
              enableAutoPatch={false}
              patchId={roundId}
              recordId={roundId || ''}
              onPatchSuccess={handleRoundPatchSuccess}
              className='cell-edit-input-manager'
              currentRecord={record.roundData}
            />
          )
        }
      },
      {
        title: 'Valuation',
        dataIndex: 'valuation',
        key: 'valuation',
        width: editingRowKey ? 280 : 200,
        minWidth: editingRowKey ? 280 : 200,
        render: (value: number | null, record: FundingRoundRow) => {
          const roundId = record.roundData?.[RoundEntryFields.rounds__id]
          const isEditing = editingRowKey === record.key

          if (!roundId) {
            // Fallback display when no roundId
            if (!value) return '-'
            const currency = getCurrencyById(record.currencyId)
            const currencySymbol = currency?.symbol || '$'
            const formattedValue = formatNumberNoDecimal(value)
            const rawValuationType = record.valuationType || 'Post'
            const valuationType = rawValuationType.charAt(0).toUpperCase() + rawValuationType.slice(1).toLowerCase()
            return (
              <div className='flex items-center justify-end gap-1 min-w-0'>
                <span className='text-gray-700 text-sm' style={{ minWidth: 0, flexShrink: 1 }}>
                  {currencySymbol}
                  {formattedValue}
                </span>
                <Tag style={{ width: 40, flexShrink: 0, textAlign: 'center' }} color={'blue'}>
                  {valuationType}
                </Tag>
              </div>
            )
          }

          // Get pre and post money valuation fields
          const preMoneyField = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.rounds__pre_money_valuation,
            isEditing,
            record.key
          )
          const postMoneyField = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.rounds__post_money_valuation,
            isEditing,
            record.key
          )

          if (!preMoneyField || !postMoneyField) {
            // Fallback display
            if (!value) return '-'
            const currency = getCurrencyById(record.currencyId)
            const currencySymbol = currency?.symbol || '$'
            const formattedValue = formatNumberNoDecimal(value)
            const rawValuationType = record.valuationType || 'Post'
            const valuationType = rawValuationType.charAt(0).toUpperCase() + rawValuationType.slice(1).toLowerCase()
            return (
              <div className='flex items-center justify-end gap-1 min-w-0'>
                <span className='text-gray-700 text-sm' style={{ minWidth: 0, flexShrink: 1 }}>
                  {currencySymbol}
                  {formattedValue}
                </span>
                <Tag style={{ width: 40, flexShrink: 0, textAlign: 'center' }} color={'blue'}>
                  {valuationType}
                </Tag>
              </div>
            )
          }

          // Create valuation field config for combined display
          const preMoneyValue = record.roundData?.[RoundEntryFields.rounds__pre_money_valuation] || null
          const postMoneyValue = record.roundData?.[RoundEntryFields.rounds__post_money_valuation] || null
          const currency = getCurrencyById(record.currencyId)
          const currencySymbol = currency?.symbol || '$'

          const valuationFieldConfig: ValuationFieldConfig = {
            key: `${roundId}-${RoundEntryFields.prog__valuation}`, // Make key unique per row
            label: 'Valuation',
            type: FieldTypeEnum.VALUATION,
            value: {
              preMoneyValue: preMoneyValue,
              postMoneyValue: postMoneyValue
            },
            editable: isEditing,
            currencySymbol: currencySymbol,
            preMoneyValue: preMoneyValue,
            postMoneyValue: postMoneyValue,
            min: 0,
            step: 0.01,
            placeholder: 'No valuation set',
            showValuationType: true,
            patchEndpoint: 'rounds',
            patchFields: [
              { sourceKey: 'preMoneyValue', targetField: 'pre_money_valuation' },
              { sourceKey: 'postMoneyValue', targetField: 'post_money_valuation' }
            ],
            valueTransformer: (val: any, currentRecord: any) => {
              const result: any = {
                preMoneyValue: val?.preMoneyValue || null,
                postMoneyValue: val?.postMoneyValue || null,
                [RoundEntryFields.rounds__pre_money_valuation]: val?.preMoneyValue || null,
                [RoundEntryFields.rounds__post_money_valuation]: val?.postMoneyValue || null
              }
              if (roundId) {
                result.id = roundId
              }
              return result
            }
          }

          if (!isEditing) {
            return (
              <div
                onClick={() => startEditing(record.key)}
                className='cursor-pointer flex items-center justify-end min-w-0'
              >
                <InputManager
                  inputMode='read'
                  labelPosition='none'
                  alignment='right'
                  fields={[valuationFieldConfig]}
                  onChange={(key, val) => handleFieldChange?.(key, val)}
                  className='cell-edit-input-manager'
                  currentRecord={record.roundData}
                />
              </div>
            )
          }

          return (
            <div className='flex items-center justify-end min-w-0'>
              <InputManager
                inputMode='edit'
                labelPosition='none'
                alignment='right'
                fields={[valuationFieldConfig]}
                onChange={(key, val) => {
                  // Track both pre and post money values (the valuation input emits a record)
                  const valuation = isRecordFieldValue(val) ? val : undefined
                  if (valuation?.preMoneyValue !== undefined) {
                    handleFieldChangeForRound(
                      record.key,
                      RoundEntryFields.rounds__pre_money_valuation,
                      valuation.preMoneyValue
                    )
                  }
                  if (valuation?.postMoneyValue !== undefined) {
                    handleFieldChangeForRound(
                      record.key,
                      RoundEntryFields.rounds__post_money_valuation,
                      valuation.postMoneyValue
                    )
                  }
                  handleFieldChange?.(key, val)
                }}
                enableAutoPatch={false}
                patchId={roundId}
                recordId={roundId || ''}
                onPatchSuccess={handleRoundPatchSuccess}
                className='cell-edit-input-manager'
                currentRecord={record.roundData}
              />
            </div>
          )
        }
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        width: editingRowKey ? 200 : 150,
        minWidth: editingRowKey ? 200 : 150,
        render: (value: number | null, record: FundingRoundRow) => {
          const roundId = record.roundData?.[RoundEntryFields.rounds__id]
          const isEditing = editingRowKey === record.key

          if (!roundId)
            return value
              ? `${getCurrencyById(record.currencyId)?.symbol || '$'}${new Intl.NumberFormat('en-US').format(value)}`
              : '-'

          const fieldConfig = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.rounds__raise_amount,
            isEditing,
            record.key
          )
          if (!fieldConfig)
            return value
              ? `${getCurrencyById(record.currencyId)?.symbol || '$'}${new Intl.NumberFormat('en-US').format(value)}`
              : '-'

          if (!isEditing) {
            return (
              <div onClick={() => startEditing(record.key)} className='cursor-pointer'>
                <InputManager
                  inputMode='read'
                  labelPosition='none'
                  alignment='right'
                  fields={[fieldConfig]}
                  onChange={(key, val) => handleFieldChange?.(key, val)}
                  className='cell-edit-input-manager'
                  currentRecord={record.roundData}
                />
              </div>
            )
          }

          return (
            <InputManager
              inputMode='edit'
              labelPosition='none'
              alignment='right'
              fields={[fieldConfig]}
              onChange={(key, val) => {
                handleFieldChangeForRound(record.key, RoundEntryFields.rounds__raise_amount, val)
                handleFieldChange?.(key, val)
              }}
              enableAutoPatch={false}
              patchId={roundId}
              recordId={roundId || ''}
              onPatchSuccess={handleRoundPatchSuccess}
              className='cell-edit-input-manager'
              currentRecord={record.roundData}
            />
          )
        }
      },
      {
        title: 'Stage',
        dataIndex: 'stage',
        key: 'stage',
        width: editingRowKey ? 200 : 150,
        minWidth: editingRowKey ? 200 : 150,
        render: (value: string, record: FundingRoundRow) => {
          const roundId = record.roundData?.[RoundEntryFields.rounds__id]
          const isEditing = editingRowKey === record.key

          if (!roundId) return value || '-'

          const fieldConfig = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.rounds__funding_stage,
            isEditing,
            record.key
          )
          if (!fieldConfig) return value || '-'

          if (!isEditing) {
            return (
              <div onClick={() => startEditing(record.key)} className='cursor-pointer'>
                <InputManager
                  inputMode='read'
                  labelPosition='none'
                  alignment='left'
                  fields={[fieldConfig]}
                  onChange={(key, val) => handleFieldChange?.(key, val)}
                  className='cell-edit-input-manager'
                  currentRecord={record.roundData}
                />
              </div>
            )
          }

          return (
            <InputManager
              inputMode='edit'
              labelPosition='none'
              alignment='left'
              fields={[fieldConfig]}
              onChange={(key, val) => {
                handleFieldChangeForRound(record.key, RoundEntryFields.rounds__funding_stage, val)
                handleFieldChange?.(key, val)
              }}
              enableAutoPatch={false}
              patchId={roundId}
              recordId={roundId || ''}
              onPatchSuccess={handleRoundPatchSuccess}
              className='cell-edit-input-manager'
              currentRecord={record.roundData}
            />
          )
        }
      },
      {
        title: 'Our Investment',
        dataIndex: 'our_investment',
        key: 'our_investment',
        width: editingRowKey ? 200 : 150,
        minWidth: editingRowKey ? 200 : 150,
        render: (value: number | null, record: FundingRoundRow) => {
          const roundId = record.roundData?.[RoundEntryFields.rounds__id]
          const isEditing = editingRowKey === record.key

          if (!roundId)
            return value
              ? `${getCurrencyById(record.currencyId)?.symbol || '$'}${new Intl.NumberFormat('en-US').format(value)}`
              : '-'

          const fieldConfig = getFieldConfigForRound(
            record.roundData,
            RoundEntryFields.prog__commitment,
            isEditing,
            record.key
          )
          if (!fieldConfig)
            return value
              ? `${getCurrencyById(record.currencyId)?.symbol || '$'}${new Intl.NumberFormat('en-US').format(value)}`
              : '-'

          if (!isEditing) {
            return (
              <div onClick={() => startEditing(record.key)} className='cursor-pointer'>
                <InputManager
                  inputMode='read'
                  labelPosition='none'
                  alignment='right'
                  fields={[fieldConfig]}
                  onChange={(key, val) => handleFieldChange?.(key, val)}
                  className='cell-edit-input-manager'
                  currentRecord={record.roundData}
                />
              </div>
            )
          }

          return (
            <InputManager
              inputMode='edit'
              labelPosition='none'
              alignment='right'
              fields={[fieldConfig]}
              onChange={(key, val) => {
                handleFieldChangeForRound(record.key, RoundEntryFields.rounds__name, val)
                handleFieldChange?.(key, val)
              }}
              enableAutoPatch={false}
              patchId={roundId}
              recordId={roundId || ''}
              onPatchSuccess={handleRoundPatchSuccess}
              className='cell-edit-input-manager'
              currentRecord={record.roundData}
            />
          )
        }
      }
    ]
  }, [
    getCurrencyById,
    handleRoundNameClick,
    getFieldConfigForRound,
    handleRoundPatchSuccess,
    handleFieldChange,
    recordId,
    editingRowKey,
    startEditing
  ])

  return (
    <div className={className}>
      <FormTable
        mode='existing'
        dataSource={fundingRounds}
        columns={columnConfig}
        title='Funding Rounds'
        showHeader={true}
        emptyText='No funding rounds available'
        size='small'
        pagination={false}
        includeActions={!!editingRowKey}
        actionsWidth={60}
        editingRowKey={editingRowKey}
        savingRowKey={savingRowKey}
        onSave={saveRow}
        onCancel={cancelEditing}
        onRemove={handleRemove}
        hideDeleteWhenNotEditing={true}
        headerActions={
          // the saved round, not the row's `|| null` display copy (a zero stays a zero)
          <ExportRowsButton
            name={`${typeof data[AccountRecordFields.accounts__name] === 'string' ? data[AccountRecordFields.accounts__name] : 'Company'} Funding Rounds`}
            rows={fundingRounds}
            columns={[
              { title: 'Name', value: (r) => r.roundData?.[RoundEntryFields.rounds__name] },
              { title: 'Date', value: (r) => r.roundData?.[RoundEntryFields.rounds__round_close_date], kind: 'date' },
              { title: 'Pre-Money', value: (r) => r.roundData?.[RoundEntryFields.rounds__pre_money_valuation] },
              { title: 'Post-Money', value: (r) => r.roundData?.[RoundEntryFields.rounds__post_money_valuation] },
              { title: 'Size', value: (r) => r.roundData?.[RoundEntryFields.rounds__raise_amount] },
              { title: 'Stage', value: (r) => (r.stage === '-' ? null : r.stage) },
              { title: 'Our Investment', value: (r) => r.roundData?.[RoundEntryFields.prog__commitment] },
              { title: 'Currency', value: (r) => getCurrencyById(r.currencyId)?.code }
            ]}
          />
        }
      />
    </div>
  )
}
